import React from 'react';
import {
  calculateCashFlow,
  calculateCompoundGrowth,
  calculateDebtPayoff,
  calculateDebtPayoffTimeline,
  calculateEmergencyFund,
  calculateGoalProgress,
  calculateInsuranceCoverage,
  calculatePortfolioDrift,
  calculateRentVsBuy,
  calculateRetirementContribution,
  calculateRetirementWithdrawal,
  calculateRiskRange,
  calculateTaxImpact,
  toNumber,
} from './interactive/calculations';
import './interactiveEmbeds.css';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const number = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const decimal = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function formatPrimitiveValue(value, format = 'number') {
  if (value == null || Number.isNaN(value)) return '—';
  if (format === 'currency') return currency.format(toNumber(value));
  if (format === 'percent') return `${number.format(toNumber(value))}%`;
  if (format === 'text') return String(value ?? '');
  if (typeof value === 'number') return number.format(value);
  return String(value ?? '');
}

function collectComposableInitialValues(primitives) {
  const entries = [];
  primitives.forEach((primitive) => {
    if (primitive.type === 'segmented_control') {
      const selectedOption = (primitive.options || []).find((option) => option.id === primitive.value);
      const selectedValue = Object.prototype.hasOwnProperty.call(selectedOption || {}, 'value')
        ? selectedOption.value
        : primitive.value;
      entries.push([primitive.id, selectedValue]);
      entries.push([`${primitive.id}__selected`, primitive.value]);
    } else if ('value' in primitive) {
      entries.push([primitive.id, primitive.value]);
    }
    (primitive.items || []).forEach((item) => entries.push([item.id, item.value]));
    (primitive.segments || []).forEach((segment) => entries.push([segment.id, segment.value]));
    if (primitive.type === 'table_summary') {
      (primitive.rows || []).forEach((row) => {
        if (row.value_id) entries.push([row.value_id, row.value]);
      });
    }
  });
  return Object.fromEntries(entries);
}

function evaluateMathExpression(expr, valuesById) {
  if (!expr || typeof expr !== 'object') return null;
  if (Object.prototype.hasOwnProperty.call(expr, 'const')) return toNumber(expr.const);
  if (expr.ref) return Object.prototype.hasOwnProperty.call(valuesById, expr.ref) ? valuesById[expr.ref] : null;

  const args = (expr.args || []).map((arg) => evaluateMathExpression(arg, valuesById));
  if (args.some((arg) => arg == null || Number.isNaN(arg))) return null;
  const nums = args.map((arg) => toNumber(arg));

  switch (expr.op) {
    case 'add':
      return nums.reduce((sum, value) => sum + value, 0);
    case 'subtract':
      return nums[0] - nums[1];
    case 'multiply':
      return nums.reduce((product, value) => product * value, 1);
    case 'divide':
      return nums[1] === 0 ? null : nums[0] / nums[1];
    case 'min':
      return Math.min(...nums);
    case 'max':
      return Math.max(...nums);
    case 'abs':
      return Math.abs(nums[0]);
    case 'round':
      return Math.round(nums[0]);
    case 'ceil':
      return Math.ceil(nums[0]);
    case 'floor':
      return Math.floor(nums[0]);
    case 'pow':
      return Math.pow(nums[0], nums[1]);
    default:
      return null;
  }
}

function resolveComposableValues(baseValues, bindings = []) {
  const resolved = { ...baseValues };
  for (let pass = 0; pass < Math.max(1, bindings.length); pass += 1) {
    bindings.forEach((binding) => {
      resolved[binding.target] = evaluateMathExpression(binding.expr, resolved);
    });
  }
  return resolved;
}

function valueFor(valuesById, id, fallback) {
  return Object.prototype.hasOwnProperty.call(valuesById, id) ? valuesById[id] : fallback;
}

function sourceLabel(source) {
  return {
    user_data: 'User data',
    user_provided: 'User provided',
    assumption: 'Assumption',
    hypothetical: 'Hypothetical',
  }[source] || source;
}

function normalizePayload(payload = {}) {
  return {
    monthlySpend: payload.monthlySpend ?? payload.monthly_spend,
    currentSavings: payload.currentSavings ?? payload.current_savings,
    targetMonths: payload.targetMonths ?? payload.target_months,
    monthlyContribution: payload.monthlyContribution ?? payload.monthly_contribution,
    debts: payload.debts,
    extraPayment: payload.extraPayment ?? payload.extra_payment,
    annualIncome: payload.annualIncome ?? payload.annual_income,
    currentContributionPct: payload.currentContributionPct ?? payload.current_contribution_pct,
    proposedContributionPct: payload.proposedContributionPct ?? payload.proposed_contribution_pct,
    employerMatchPct: payload.employerMatchPct ?? payload.employer_match_pct,
    annualReturnPct: payload.annualReturnPct ?? payload.annual_return_pct,
    initialAmount: payload.initialAmount ?? payload.initial_amount,
    monthlyIncome: payload.monthlyIncome ?? payload.monthly_income,
    fixedExpenses: payload.fixedExpenses ?? payload.fixed_expenses,
    variableExpenses: payload.variableExpenses ?? payload.variable_expenses,
    savings: payload.savings,
    targetSavingsRatePct: payload.targetSavingsRatePct ?? payload.target_savings_rate_pct,
    categories: payload.categories || [],
    goalName: payload.goalName ?? payload.goal_name,
    currentAmount: payload.currentAmount ?? payload.current_amount,
    targetAmount: payload.targetAmount ?? payload.target_amount,
    targetDate: payload.targetDate ?? payload.target_date,
    monthlyRent: payload.monthlyRent ?? payload.monthly_rent,
    homePrice: payload.homePrice ?? payload.home_price,
    downPayment: payload.downPayment ?? payload.down_payment,
    mortgageRatePct: payload.mortgageRatePct ?? payload.mortgage_rate_pct,
    propertyTaxPct: payload.propertyTaxPct ?? payload.property_tax_pct,
    maintenancePct: payload.maintenancePct ?? payload.maintenance_pct,
    homeAppreciationPct: payload.homeAppreciationPct ?? payload.home_appreciation_pct,
    rentGrowthPct: payload.rentGrowthPct ?? payload.rent_growth_pct,
    allocations: payload.allocations || [],
    riskLevel: payload.riskLevel ?? payload.risk_level,
    deductionAmount: payload.deductionAmount ?? payload.deduction_amount,
    scenarioType: payload.scenarioType ?? payload.scenario_type,
    marginalTaxRatePct: payload.marginalTaxRatePct ?? payload.marginal_tax_rate_pct,
    stateTaxRatePct: payload.stateTaxRatePct ?? payload.state_tax_rate_pct,
    saleAmount: payload.saleAmount ?? payload.sale_amount,
    costBasis: payload.costBasis ?? payload.cost_basis,
    capitalGainsTaxRatePct: payload.capitalGainsTaxRatePct ?? payload.capital_gains_tax_rate_pct,
    extraIncome: payload.extraIncome ?? payload.extra_income,
    conversionAmount: payload.conversionAmount ?? payload.conversion_amount,
    scenarioLabel: payload.scenarioLabel ?? payload.scenario_label,
    dependents: payload.dependents,
    yearsNeeded: payload.yearsNeeded ?? payload.years_needed,
    existingCoverage: payload.existingCoverage ?? payload.existing_coverage,
    liquidAssets: payload.liquidAssets ?? payload.liquid_assets,
    educationGoal: payload.educationGoal ?? payload.education_goal,
    portfolioValue: payload.portfolioValue ?? payload.portfolio_value,
    annualSpending: payload.annualSpending ?? payload.annual_spending,
    withdrawalRatePct: payload.withdrawalRatePct ?? payload.withdrawal_rate_pct,
    withdrawalMode: payload.withdrawalMode ?? payload.withdrawal_mode,
    expectedReturnPct: payload.expectedReturnPct ?? payload.expected_return_pct ?? payload.annualReturnPct ?? payload.annual_return_pct,
    volatilityPct: payload.volatilityPct ?? payload.volatility_pct,
    inflationPct: payload.inflationPct ?? payload.inflation_pct,
    years: payload.years,
    steps: payload.steps || [],
    scenarios: payload.scenarios || [],
  };
}

function AppletShell({ embed, children, details }) {
  const [expanded, setExpanded] = React.useState(false);
  const assumptions = embed.assumptions || [];
  const provenance = embed.provenance || [];

  return (
    <section className="interactive-embed" data-testid={`interactive-${embed.template}`}>
      <div className="interactive-header">
        <div>
          <div className="interactive-kicker">{embed.mode === 'explainer' ? 'Interactive explainer' : 'Guided calculator'}</div>
          <h3>{embed.title}</h3>
        </div>
        <button
          type="button"
          className="interactive-icon-btn"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide' : 'Details'}
        </button>
      </div>
      {embed.summary && <p className="interactive-summary">{embed.summary}</p>}
      {children}
      {expanded && (
        <div className="interactive-details">
          {details}
          {assumptions.length > 0 && (
            <div>
              <div className="interactive-label">Assumptions</div>
              <ul>
                {assumptions.map((assumption, index) => (
                  <li key={`${index}-${typeof assumption === 'string' ? assumption : assumption.label}`}>
                    {typeof assumption === 'string' ? assumption : `${assumption.label}: ${assumption.value}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {provenance.length > 0 && (
            <div>
              <div className="interactive-label">Data provenance</div>
              <ul>
                {provenance.map((item) => (
                  <li key={item.id}>
                    <strong>{sourceLabel(item.source)}:</strong> {item.label}
                    {item.assumption ? ` (${item.assumption})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function RangeControl({ label, value, min, max, step = 1, testId, onChange, suffix = '' }) {
  const handleChange = (event) => onChange(toNumber(event.target.value));

  return (
    <label className="interactive-control">
      <span>{label}</span>
      <strong>{number.format(value)}{suffix}</strong>
      <input
        data-testid={testId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onInput={handleChange}
        onChange={handleChange}
      />
    </label>
  );
}

function Metric({ label, value }) {
  return (
    <div className="interactive-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function LabeledTrack({ label, value, max, caption, tone = 'default' }) {
  const width = max > 0 ? Math.min(100, (Math.max(0, value) / max) * 100) : 0;

  return (
    <div className="interactive-track-row">
      <div className="interactive-track-label">
        <strong>{label}</strong>
        {caption && <span>{caption}</span>}
      </div>
      <div className="interactive-comparison-track">
        <span className={tone} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function EmergencyFundApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [targetMonths, setTargetMonths] = React.useState(toNumber(payload.targetMonths, 9));
  const result = calculateEmergencyFund({ ...payload, targetMonths });
  const currentSavings = toNumber(payload.currentSavings);
  const runwayScale = Math.max(result.monthsCovered, targetMonths, 1);
  const currentRunwayWidth = Math.min(100, (result.monthsCovered / runwayScale) * 100);
  const targetRunwayWidth = Math.min(100, (targetMonths / runwayScale) * 100);
  const statusLabel = result.isAtOrAboveTarget
    ? `${decimal.format(result.surplusMonths)} months above target`
    : `${currency.format(result.gap)} gap`;
  const statusWord = result.isAtOrAboveTarget ? 'Covered' : 'Needs funding';
  const hasMonthlyContribution = toNumber(payload.monthlyContribution) > 0;
  const secondaryMetric = !result.isAtOrAboveTarget && hasMonthlyContribution
    ? <Metric label="Time to target" value={`${result.monthsToTarget} months`} />
    : null;

  return (
    <AppletShell
      embed={embed}
      details={<p>Adjust the reserve target to compare current cash coverage with the recommended number of months.</p>}
    >
      <div className="emergency-snapshot">
        <div className={`emergency-status ${result.isAtOrAboveTarget ? 'covered' : 'short'}`}>
          <span>{statusWord}</span>
          <strong>{decimal.format(result.monthsCovered)} months covered</strong>
          <small>Current runway</small>
        </div>
        <div className="emergency-target-summary">
          <span>Target runway</span>
          <strong>{decimal.format(targetMonths)} months</strong>
          <small>Reserve target: {currency.format(result.targetAmount)}</small>
        </div>
      </div>

      <div className="emergency-runway" aria-label="Current emergency fund runway compared with target runway">
        <div className="emergency-runway-row">
          <div className="emergency-runway-label">
            <strong>Current runway</strong>
            <span>{currency.format(currentSavings)} cash reserve</span>
          </div>
          <div className="emergency-runway-track">
            <span className="current" style={{ width: `${currentRunwayWidth}%` }} />
          </div>
          <strong>{decimal.format(result.monthsCovered)} mo</strong>
        </div>
        <div className="emergency-runway-row">
          <div className="emergency-runway-label">
            <strong>Target runway</strong>
            <span>{currency.format(result.targetAmount)} reserve target</span>
          </div>
          <div className="emergency-runway-track">
            <span className="target" style={{ width: `${targetRunwayWidth}%` }} />
          </div>
          <strong>{decimal.format(targetMonths)} mo</strong>
        </div>
      </div>

      <div className="emergency-slider-panel">
        <div className="emergency-slider-copy">
          <strong>Adjust target months</strong>
          <span>Drag the slider to test a leaner or more conservative reserve.</span>
        </div>
        <RangeControl
          label="Target months"
          value={targetMonths}
          min={3}
          max={18}
          testId="emergency-target-months"
          onChange={setTargetMonths}
        />
      </div>

      <div className={`interactive-metrics ${secondaryMetric ? '' : 'full-width'}`}>
        <Metric label={result.isAtOrAboveTarget ? 'Cushion above target' : 'Funding gap'} value={statusLabel} />
        {secondaryMetric}
      </div>
    </AppletShell>
  );
}

function DebtPayoffApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [extraPayment, setExtraPayment] = React.useState(toNumber(payload.extraPayment));
  const result = calculateDebtPayoff({ debts: payload.debts, extraPayment });
  const timeline = calculateDebtPayoffTimeline({ debts: payload.debts, extraPayment });
  const maxDebt = Math.max(result.totalBalance, 1);

  return (
    <AppletShell embed={embed} details={<p>Debts are ordered by APR so extra dollars attack the highest guaranteed return first.</p>}>
      <div className="interactive-list">
        {result.payoffOrder.map((debt, index) => (
          <div key={debt.name} className="interactive-list-row debt-payoff-row">
            <span className="debt-payoff-copy">
              <span className="debt-payoff-name">{index + 1}. {debt.name}</span>
              <small className="debt-payoff-balance">{currency.format(debt.balance)} balance</small>
            </span>
            <strong>{number.format(debt.apr)}% APR</strong>
          </div>
        ))}
      </div>
      <div className="interactive-comparison" aria-label="Paydown over time">
        <div className="interactive-label">Paydown over time</div>
        {timeline.map((point) => (
          <LabeledTrack
            key={point.label}
            label={point.label}
            value={point.balance}
            max={maxDebt}
            caption={point.month === 0 ? currency.format(point.balance) : `${currency.format(point.balance)} remaining`}
            tone={point.balance === 0 ? 'growth' : 'current'}
          />
        ))}
      </div>
      <RangeControl
        label="Extra monthly payment"
        value={extraPayment}
        min={0}
        max={2000}
        step={50}
        suffix=""
        testId="debt-extra-payment"
        onChange={setExtraPayment}
      />
      <div className="interactive-metrics">
        <Metric label="Total debt" value={currency.format(result.totalBalance)} />
        <Metric label="Estimated payoff" value={result.estimatedMonths == null ? 'Add payment' : `${result.estimatedMonths} months`} />
      </div>
    </AppletShell>
  );
}

function RetirementContributionApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [proposedPct, setProposedPct] = React.useState(toNumber(payload.proposedContributionPct));
  const result = calculateRetirementContribution({ ...payload, proposedContributionPct: proposedPct });
  const currentPct = toNumber(payload.currentContributionPct);
  const currentWidth = result.proposedFutureValue > 0 ? Math.max(8, (result.currentFutureValue / result.proposedFutureValue) * 100) : 0;

  return (
    <AppletShell embed={embed} details={<p>This shows contribution impact before taxes and investment expenses.</p>}>
      <RangeControl
        label="Contribution"
        value={proposedPct}
        min={0}
        max={25}
        suffix="%"
        testId="retirement-proposed-pct"
        onChange={setProposedPct}
      />
      <div className="interactive-comparison" aria-label="Current and proposed contribution comparison">
        <div className="interactive-comparison-row">
          <div className="interactive-comparison-meta">
            <strong>Current {number.format(currentPct)}%</strong>
            <span>Annual contribution {currency.format(result.annualCurrent)}</span>
          </div>
          <div className="interactive-comparison-track">
            <span className="current" style={{ width: `${currentWidth}%` }} />
          </div>
          <strong>{currency.format(result.currentFutureValue)}</strong>
        </div>
        <div className="interactive-comparison-row">
          <div className="interactive-comparison-meta">
            <strong>Proposed {number.format(proposedPct)}%</strong>
            <span>Annual contribution {currency.format(result.annualProposed)}</span>
          </div>
          <div className="interactive-comparison-track">
            <span className="proposed" style={{ width: '100%' }} />
          </div>
          <strong>{currency.format(result.proposedFutureValue)}</strong>
        </div>
        <div className="interactive-axis-row">
          <span>Projected {number.format(payload.years)}-year value</span>
          <span>Before taxes and fees</span>
        </div>
      </div>
      <div className="interactive-metrics">
        <Metric label="Monthly paycheck impact" value={currency.format(result.monthlyPaycheckImpact)} />
        <Metric label="Projected upside" value={currency.format(result.projectedUpside)} />
      </div>
    </AppletShell>
  );
}

function CompoundGrowthApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [monthlyContribution, setMonthlyContribution] = React.useState(toNumber(payload.monthlyContribution));
  const result = calculateCompoundGrowth({ ...payload, monthlyContribution });
  const steps = payload.steps.length > 0 ? payload.steps : [{ title: 'Time compounds the habit', body: 'The earlier dollars have the longest runway.' }];
  const currentStep = steps[Math.min(stepIndex, steps.length - 1)];
  const milestones = [5, 15, toNumber(payload.years, 30)]
    .filter((year, index, values) => year > 0 && values.indexOf(year) === index)
    .map((year) => {
      const point = result.series.find((item) => item.year >= year) || result.series[result.series.length - 1];
      const contributionValue = toNumber(payload.initialAmount) + monthlyContribution * point.year * 12;
      return {
        year: point.year,
        balance: point.balance,
        contributions: contributionValue,
        growth: Math.max(0, point.balance - contributionValue),
      };
    });
  const maxMilestoneBalance = Math.max(...milestones.map((item) => item.balance), 1);

  return (
    <AppletShell embed={embed} details={<p>The curve uses the return and contribution assumptions from the assistant response.</p>}>
      <StoryProgress current={stepIndex + 1} total={steps.length} />
      <div className="interactive-growth-chart" aria-label="Compound growth by milestone">
        <div className="interactive-growth-legend">
          <span><i className="contributions" /> Contributions</span>
          <span><i className="growth" /> Investment growth</span>
        </div>
        <div className="interactive-growth-bars">
          {milestones.map((milestone) => {
            const contributionWidth = (milestone.contributions / maxMilestoneBalance) * 100;
            const growthWidth = (milestone.growth / maxMilestoneBalance) * 100;
            return (
              <div key={milestone.year} className="interactive-growth-row">
                <span>Year {milestone.year}</span>
                <div className="interactive-stacked-track">
                  <span className="contributions" style={{ width: `${contributionWidth}%` }} />
                  <span className="growth" style={{ width: `${growthWidth}%` }} />
                </div>
                <strong>{currency.format(milestone.balance)}</strong>
                <span className="interactive-growth-share">Growth share {decimal.format(milestone.balance > 0 ? (milestone.growth / milestone.balance) * 100 : 0)}%</span>
              </div>
            );
          })}
        </div>
      </div>
      <RangeControl
        label="Monthly contribution"
        value={monthlyContribution}
        min={0}
        max={2000}
        step={50}
        testId="compound-monthly-contribution"
        onChange={setMonthlyContribution}
      />
      <div className="interactive-story-copy">
        <strong>{currentStep.title}</strong>
        <p>{currentStep.body}</p>
      </div>
      <div className="interactive-story-actions">
        <button type="button" disabled={stepIndex === 0} onClick={() => setStepIndex((value) => Math.max(0, value - 1))}>Back</button>
        <button type="button" disabled={stepIndex >= steps.length - 1} onClick={() => setStepIndex((value) => Math.min(steps.length - 1, value + 1))}>Next</button>
      </div>
      <div className="interactive-metrics">
        <Metric label="Final balance" value={currency.format(result.finalBalance)} />
        <Metric label="Growth" value={currency.format(result.growth)} />
      </div>
    </AppletShell>
  );
}

function DiversificationRiskApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [stepIndex, setStepIndex] = React.useState(0);
  const ranges = calculateRiskRange({ scenarios: payload.scenarios });
  const steps = payload.steps.length > 0 ? payload.steps : [{ title: 'Diversification narrows the surprise', body: 'A basket can lower the damage from one bad asset.' }];
  const currentStep = steps[Math.min(stepIndex, steps.length - 1)];

  return (
    <AppletShell embed={embed} details={<p>Risk bands are simplified one-volatility ranges for comparison, not guarantees.</p>}>
      <StoryProgress current={stepIndex + 1} total={steps.length} />
      <div className="interactive-risk-list" aria-label="Expected return and volatility comparison">
        {ranges.map((range) => (
          <div key={range.label} className="interactive-risk-card">
            <div className="interactive-risk-card-header">
              <strong>{range.label}</strong>
              <span>{number.format(range.allocationPct)}% allocation</span>
            </div>
            <div className="interactive-risk-stats">
              <Metric label="Expected return" value={`${decimal.format(range.expectedReturn)}%`} />
              <Metric label="Volatility" value={`${decimal.format(range.volatility)}%`} />
              <Metric label="Typical range" value={`${decimal.format(range.downside)}% to ${decimal.format(range.upside)}%`} />
              <Metric label="Return per unit of risk" value={decimal.format(range.returnPerRisk)} />
            </div>
            <div className="interactive-risk-scale">
              <div className="interactive-risk-scale-labels">
                <span>Lower return</span>
                <span>Higher return</span>
              </div>
              <div className="interactive-risk-track">
                <span
                  className="range"
                  style={{
                    left: `${range.rangeStartPct}%`,
                    width: `${Math.min(range.rangeWidthPct, 100 - range.rangeStartPct)}%`,
                  }}
                />
                <i style={{ left: `${range.expectedPct}%` }} />
              </div>
              <div className="interactive-risk-scale-labels values">
                <span>{decimal.format(range.downside)}%</span>
                <span>{decimal.format(range.expectedReturn)}% expected</span>
                <span>{decimal.format(range.upside)}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="interactive-story-copy">
        <strong>{currentStep.title}</strong>
        <p>{currentStep.body}</p>
      </div>
      <div className="interactive-story-actions">
        <button type="button" disabled={stepIndex === 0} onClick={() => setStepIndex((value) => Math.max(0, value - 1))}>Back</button>
        <button type="button" disabled={stepIndex >= steps.length - 1} onClick={() => setStepIndex((value) => Math.min(steps.length - 1, value + 1))}>Next</button>
      </div>
    </AppletShell>
  );
}

function SpendingCashFlowApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [targetRate, setTargetRate] = React.useState(toNumber(payload.targetSavingsRatePct, 20));
  const result = calculateCashFlow({ ...payload, targetSavingsRatePct: targetRate });
  const max = Math.max(toNumber(payload.monthlyIncome), result.monthlyOutflow, 1);

  return (
    <AppletShell
      embed={embed}
      details={(
        <>
          <p>Fixed expenses are recurring bills that are hard to change quickly. Variable expenses are flexible spending that moves month to month. Savings is money intentionally set aside after spending.</p>
        </>
      )}
    >
      <div className="interactive-comparison" aria-label="Cash flow map">
        <LabeledTrack label="Income" value={payload.monthlyIncome} max={max} caption={currency.format(payload.monthlyIncome)} tone="proposed" />
        <LabeledTrack label="Fixed expenses" value={payload.fixedExpenses} max={max} caption={currency.format(payload.fixedExpenses)} tone="current" />
        <LabeledTrack label="Variable expenses" value={payload.variableExpenses} max={max} caption={currency.format(payload.variableExpenses)} tone="warning" />
        <LabeledTrack label="Savings" value={payload.savings} max={max} caption={`${currency.format(payload.savings)} (${decimal.format(result.savingsRatePct)}%)`} tone="growth" />
      </div>
      <RangeControl
        label="Target savings rate"
        value={targetRate}
        min={0}
        max={50}
        suffix="%"
        testId="cash-flow-target-rate"
        onChange={setTargetRate}
      />
      <div className="interactive-metrics">
        <Metric label="Target savings" value={currency.format(result.targetSavingsAmount)} />
        <Metric label="Savings gap" value={currency.format(result.savingsGap)} />
      </div>
    </AppletShell>
  );
}

function GoalProgressApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [monthlyContribution, setMonthlyContribution] = React.useState(toNumber(payload.monthlyContribution));
  const result = calculateGoalProgress({ ...payload, monthlyContribution });

  return (
    <AppletShell embed={embed} details={<p>Progress is based on the current balance, target amount, contribution pace, and target month.</p>}>
      <div className={`goal-hero ${result.isOnPace ? 'on-pace' : 'behind'}`}>
        <span>{result.isOnPace ? 'On pace' : 'Behind pace'}</span>
        <strong>{payload.goalName || embed.title}</strong>
        <small>{currency.format(result.remaining)} remaining by {payload.targetDate}</small>
      </div>
      <div className="interactive-comparison" aria-label="Progress to goal">
        <LabeledTrack
          label="Progress to goal"
          value={payload.currentAmount}
          max={payload.targetAmount}
          caption={`${decimal.format(result.progressPct)}% funded`}
          tone="proposed"
        />
      </div>
      <RangeControl
        label="Monthly contribution"
        value={monthlyContribution}
        min={0}
        max={5000}
        step={100}
        testId="goal-monthly-contribution"
        onChange={setMonthlyContribution}
      />
      <div className="interactive-metrics">
        <Metric label="Current balance" value={currency.format(payload.currentAmount)} />
        <Metric label="Time to goal" value={result.monthsToGoal == null ? 'Add contribution' : `${result.monthsToGoal} months`} />
      </div>
    </AppletShell>
  );
}

function RentVsBuyApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [years, setYears] = React.useState(toNumber(payload.years, 7));
  const result = calculateRentVsBuy({ ...payload, years });
  const max = Math.max(result.totalRent, result.netBuyCost, 1);
  const betterLabel = result.buyAdvantage > 0 ? 'Buying is lower net cost' : 'Renting is lower net cost';

  return (
    <AppletShell embed={embed} details={<p>Buying includes mortgage payments, estimated taxes, maintenance, and home appreciation.</p>}>
      <div className="interactive-comparison" aria-label="Cost bridge">
        <div className="interactive-label">Cost bridge</div>
        <LabeledTrack label="Total rent" value={result.totalRent} max={max} caption={currency.format(result.totalRent)} tone="current" />
        <LabeledTrack label="Ownership cost" value={result.ownershipCosts} max={result.ownershipCosts} caption={currency.format(result.ownershipCosts)} tone="warning" />
        <LabeledTrack label="Equity gain" value={result.equityGain} max={result.ownershipCosts} caption={`-${currency.format(result.equityGain)}`} tone="growth" />
        <LabeledTrack label="Net buy cost" value={result.netBuyCost} max={result.ownershipCosts} caption={currency.format(result.netBuyCost)} tone="proposed" />
      </div>
      <RangeControl
        label="Years in home"
        value={years}
        min={1}
        max={15}
        testId="rent-buy-years"
        onChange={setYears}
      />
      <div className="interactive-metrics">
        <Metric label="Breakeven check" value={betterLabel} />
        <Metric label="Monthly mortgage" value={currency.format(result.monthlyMortgage)} />
        <Metric label="Total rent" value={currency.format(result.totalRent)} />
        <Metric label="Net buy cost" value={currency.format(result.netBuyCost)} />
      </div>
    </AppletShell>
  );
}

function PortfolioAllocationApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const rows = calculatePortfolioDrift({ allocations: payload.allocations });

  return (
    <AppletShell embed={embed} details={<p>Allocation drift compares the current mix against an illustrative model target for the stated risk level.</p>}>
      <div className="portfolio-risk-badge">Risk level: <strong>{payload.riskLevel}</strong> · Target is illustrative, not a personalized mandate.</div>
      <div className="interactive-comparison" aria-label="Current mix and target mix">
        {rows.map((row) => (
          <div key={row.label} className="allocation-row">
            <div className="interactive-comparison-meta">
              <strong>{row.label}</strong>
              <span>{row.driftPct === 0 ? 'On target' : `${decimal.format(Math.abs(row.driftPct))} pts ${row.driftPct > 0 ? 'over' : 'under'} target`}</span>
            </div>
            <div className="allocation-bars">
              <LabeledTrack label="Current mix" value={row.currentPct} max={100} caption={`${decimal.format(row.currentPct)}%`} tone="current" />
              <LabeledTrack label="Illustrative target mix" value={row.targetPct} max={100} caption={`${decimal.format(row.targetPct)}%`} tone="proposed" />
            </div>
          </div>
        ))}
      </div>
    </AppletShell>
  );
}

function TaxImpactApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const scenarioType = payload.scenarioType || 'deduction';
  const initialAmount = scenarioType === 'capital_gain_sale'
    ? toNumber(payload.saleAmount)
    : scenarioType === 'ordinary_income'
      ? toNumber(payload.extraIncome)
      : scenarioType === 'roth_conversion'
        ? toNumber(payload.conversionAmount)
        : toNumber(payload.deductionAmount);
  const [scenarioAmount, setScenarioAmount] = React.useState(initialAmount);
  const result = calculateTaxImpact({
    ...payload,
    saleAmount: scenarioType === 'capital_gain_sale' ? scenarioAmount : payload.saleAmount,
    extraIncome: scenarioType === 'ordinary_income' ? scenarioAmount : payload.extraIncome,
    conversionAmount: scenarioType === 'roth_conversion' ? scenarioAmount : payload.conversionAmount,
    deductionAmount: scenarioType === 'deduction' ? scenarioAmount : payload.deductionAmount,
  });
  const scenarioLabel = {
    deduction: 'Deduction',
    capital_gain_sale: 'Capital gain sale',
    ordinary_income: 'Ordinary income',
    roth_conversion: 'Roth conversion',
  }[scenarioType] || 'Tax scenario';

  return (
    <AppletShell embed={embed} details={<p>This simplified estimate adapts to deductions, capital gain sales, ordinary income, and Roth conversions using the scenario fields supplied by the assistant.</p>}>
      <div className="portfolio-risk-badge">Scenario type: <strong>{scenarioLabel}</strong></div>
      <div className="tax-equation" aria-label="Tax impact equation">
        <span>{currency.format(result.taxableAmount)} taxable amount</span>
        <strong>x</strong>
        <span>{decimal.format(result.totalTaxRatePct)}% tax rate</span>
        <strong>=</strong>
        <span>{currency.format(Math.abs(result.estimatedTaxEffect))} {result.estimatedTaxEffect < 0 ? 'saved' : 'due'}</span>
      </div>
      <RangeControl
        label={payload.scenarioLabel || scenarioLabel}
        value={scenarioAmount}
        min={0}
        max={scenarioType === 'capital_gain_sale' ? 150000 : 30000}
        step={500}
        testId="tax-deduction"
        onChange={setScenarioAmount}
      />
      <div className="interactive-metrics">
        <Metric label="Estimated tax effect" value={`${result.estimatedTaxEffect < 0 ? '-' : ''}${currency.format(Math.abs(result.estimatedTaxEffect))}`} />
        <Metric label="After-tax result" value={currency.format(result.afterTaxResult)} />
      </div>
    </AppletShell>
  );
}

function InsuranceCoverageApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [yearsNeeded, setYearsNeeded] = React.useState(toNumber(payload.yearsNeeded, 10));
  const result = calculateInsuranceCoverage({ ...payload, yearsNeeded });

  return (
    <AppletShell embed={embed} details={<p>Coverage need includes income replacement, debts, education goals, and liquid asset offsets.</p>}>
      <div className="interactive-comparison" aria-label="Insurance coverage gap">
        <LabeledTrack label="Existing coverage" value={payload.existingCoverage} max={result.recommendedCoverage} caption={currency.format(payload.existingCoverage)} tone="current" />
        <LabeledTrack label="Recommended coverage" value={result.recommendedCoverage} max={result.recommendedCoverage} caption={currency.format(result.recommendedCoverage)} tone="proposed" />
      </div>
      <RangeControl
        label="Income replacement years"
        value={yearsNeeded}
        min={1}
        max={30}
        testId="insurance-years-needed"
        onChange={setYearsNeeded}
      />
      <div className="interactive-metrics">
        <Metric label="Coverage gap" value={currency.format(result.coverageGap)} />
        <Metric label="Dependents" value={number.format(payload.dependents)} />
      </div>
    </AppletShell>
  );
}

function RetirementWithdrawalApplet({ embed }) {
  const payload = normalizePayload(embed.payload);
  const [annualSpending, setAnnualSpending] = React.useState(toNumber(payload.annualSpending));
  const result = calculateRetirementWithdrawal({ ...payload, annualSpending });
  const maxRate = Math.max(result.actualWithdrawalRatePct, toNumber(payload.withdrawalRatePct), 1);

  return (
    <AppletShell embed={embed} details={<p>Simulation uses deterministic market-return paths from the expected return, volatility, inflation, withdrawal mode, and time horizon assumptions.</p>}>
      <div className="withdrawal-hero">
        <span>Success likelihood</span>
        <strong>{decimal.format(result.successProbabilityPct)}%</strong>
        <small>{number.format(payload.years)} years · {payload.withdrawalMode === 'percent_of_portfolio' ? 'Percent of portfolio' : 'Fixed annual amount'}</small>
      </div>
      <div className="interactive-comparison" aria-label="Withdrawal sustainability">
        <LabeledTrack label="Withdrawal rate" value={result.actualWithdrawalRatePct} max={maxRate} caption={`${decimal.format(result.actualWithdrawalRatePct)}% of portfolio`} tone={result.isAbovePlannedRate ? 'warning' : 'proposed'} />
        <LabeledTrack label="Target rate" value={payload.withdrawalRatePct} max={maxRate} caption={`${decimal.format(payload.withdrawalRatePct)}% planning guardrail`} tone="current" />
      </div>
      <RangeControl
        label="Annual spending"
        value={annualSpending}
        min={0}
        max={150000}
        step={1000}
        testId="withdrawal-spending"
        onChange={setAnnualSpending}
      />
      <div className="interactive-metrics">
        <Metric label="Median ending balance" value={currency.format(result.medianEndingBalance)} />
        <Metric label="Poor-market balance" value={currency.format(result.poorMarketEndingBalance)} />
        <Metric label="Expected return / volatility" value={`${decimal.format(payload.expectedReturnPct)}% / ${decimal.format(payload.volatilityPct)}%`} />
        <Metric label="Annual spending" value={currency.format(annualSpending)} />
      </div>
    </AppletShell>
  );
}

function StoryProgress({ current, total }) {
  return (
    <div className="interactive-story-progress" aria-label={`Step ${current} of ${total}`}>
      {Array.from({ length: total }).map((_, index) => (
        <span key={index} className={index < current ? 'active' : ''} />
      ))}
    </div>
  );
}

function ComposableApplet({ embed }) {
  const primitives = React.useMemo(() => embed.payload?.primitives || [], [embed.payload]);
  const [values, setValues] = React.useState(() => collectComposableInitialValues(primitives));
  const resolvedValues = React.useMemo(
    () => resolveComposableValues(values, embed.payload?.bindings || []),
    [values, embed.payload]
  );

  const setPrimitiveValue = (id, value, selectedId) => {
    // TEMP_EMBED_DEBUG: intentionally verbose browser diagnostics for composable applet state.
    // Remove after the embed generation/rendering issue is resolved.
    console.debug('TEMP_EMBED_DEBUG composable_control_change', {
      template: embed.template,
      title: embed.title,
      id,
      value,
      selectedId,
    });
    setValues((current) => ({
      ...current,
      [id]: value,
      ...(selectedId == null ? {} : { [`${id}__selected`]: selectedId }),
    }));
  };

  React.useEffect(() => {
    const bindings = embed.payload?.bindings || [];
    // TEMP_EMBED_DEBUG: intentionally verbose browser diagnostics for composable applet math.
    // Remove after the embed generation/rendering issue is resolved.
    console.debug('TEMP_EMBED_DEBUG composable_resolved_values', {
      template: embed.template,
      title: embed.title,
      primitiveCount: primitives.length,
      primitives: primitives.map((primitive) => ({
        id: primitive.id,
        type: primitive.type,
        label: primitive.label,
        value: primitive.value,
      })),
      bindingCount: bindings.length,
      bindings,
      baseValues: values,
      resolvedValues,
      nullOrNaNValues: Object.fromEntries(
        Object.entries(resolvedValues).filter(([, value]) => value == null || Number.isNaN(value))
      ),
    });
  }, [embed, primitives, resolvedValues, values]);

  return (
    <AppletShell
      embed={embed}
      details={<p>Adjust the controls to compare the scenario values. Assumptions and data sources are listed below when available.</p>}
    >
      <div className="composable-grid">
        {primitives.map((primitive) => (
          <ComposablePrimitiveRenderer
            key={primitive.id}
            primitive={primitive}
            valuesById={resolvedValues}
            setValue={(value, selectedId) => setPrimitiveValue(primitive.id, value, selectedId)}
          />
        ))}
      </div>
    </AppletShell>
  );
}

function ComposablePrimitiveRenderer({ primitive, valuesById, setValue }) {
  if (primitive.type === 'metric') {
    const current = valueFor(valuesById, primitive.id, primitive.value);
    return (
      <div className="interactive-metric composable-metric">
        <span>{primitive.label}</span>
        <strong>{formatPrimitiveValue(current, primitive.format)}</strong>
        {primitive.caption && <small>{primitive.caption}</small>}
      </div>
    );
  }

  if (primitive.type === 'slider') {
    const current = toNumber(valueFor(valuesById, primitive.id, primitive.value), primitive.value);
    return (
      <label className="interactive-control composable-control">
        <span>{primitive.label}</span>
        <strong>{formatPrimitiveValue(current, primitive.format)}</strong>
        <input
          data-testid={`composable-slider-${primitive.id}`}
          type="range"
          min={primitive.min}
          max={primitive.max}
          step={primitive.step}
          value={current}
          onInput={(event) => setValue(toNumber(event.target.value))}
          onChange={(event) => setValue(toNumber(event.target.value))}
        />
      </label>
    );
  }

  if (primitive.type === 'segmented_control') {
    const currentSelection = valueFor(valuesById, `${primitive.id}__selected`, primitive.value);
    return (
      <div className="composable-segmented">
        <div className="interactive-label">{primitive.label}</div>
        <div className="composable-segmented-options">
          {(primitive.options || []).map((option) => {
            const optionValue = Object.prototype.hasOwnProperty.call(option, 'value') ? option.value : option.id;
            return (
              <button
                key={option.id}
                type="button"
                data-testid={`composable-segment-${primitive.id}-${option.id}`}
                aria-pressed={currentSelection === option.id}
                onClick={() => setValue(optionValue, option.id)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (primitive.type === 'comparison_track') {
    const resolvedItems = (primitive.items || []).map((item) => ({
      ...item,
      resolvedValue: valueFor(valuesById, item.id, item.value),
    }));
    const max = Math.max(...resolvedItems.map((item) => toNumber(item.resolvedValue)), 1);
    return (
      <div className="interactive-comparison composable-wide">
        <div className="interactive-label">{primitive.label}</div>
        {resolvedItems.map((item) => (
          <LabeledTrack
            key={item.id}
            label={item.label}
            value={toNumber(item.resolvedValue)}
            max={max}
            caption={formatPrimitiveValue(item.resolvedValue, item.format || primitive.format)}
            tone={item.tone}
          />
        ))}
      </div>
    );
  }

  if (primitive.type === 'stacked_bar') {
    const resolvedSegments = (primitive.segments || []).map((segment) => ({
      ...segment,
      resolvedValue: valueFor(valuesById, segment.id, segment.value),
    }));
    const total = resolvedSegments.reduce((sum, segment) => sum + toNumber(segment.resolvedValue), 0);
    return (
      <div className="composable-stacked composable-wide">
        <div className="interactive-label">{primitive.label}</div>
        <div className="interactive-stacked-track">
          {resolvedSegments.map((segment) => (
            <span
              key={segment.id}
              className={segment.tone || 'default'}
              style={{ width: `${total > 0 ? (toNumber(segment.resolvedValue) / total) * 100 : 0}%` }}
              title={`${segment.label}: ${formatPrimitiveValue(segment.resolvedValue, segment.format || primitive.format)}`}
            />
          ))}
        </div>
        <div className="composable-legend">
          {resolvedSegments.map((segment) => (
            <span key={segment.id}><i className={segment.tone || 'default'} />{segment.label}: {formatPrimitiveValue(segment.resolvedValue, segment.format || primitive.format)}</span>
          ))}
        </div>
      </div>
    );
  }

  if (primitive.type === 'mini_line') {
    const points = primitive.points || [];
    const values = points.map((point) => toNumber(point.y));
    const min = Math.min(...values, 0);
    const max = Math.max(...values, 1);
    const range = Math.max(max - min, 1);
    const polyline = points.map((point, index) => {
      const x = points.length > 1 ? (index / (points.length - 1)) * 100 : 0;
      const y = 36 - ((toNumber(point.y) - min) / range) * 32;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="composable-mini-line">
        <div className="interactive-label">{primitive.label}</div>
        <svg viewBox="0 0 100 40" role="img" aria-label={primitive.label} preserveAspectRatio="none">
          <polyline points={polyline} />
        </svg>
        <div className="interactive-axis-row">
          <span>{points[0]?.x}</span>
          <strong>{formatPrimitiveValue(values[values.length - 1], primitive.format)}</strong>
          <span>{points[points.length - 1]?.x}</span>
        </div>
      </div>
    );
  }

  if (primitive.type === 'stepper') {
    const current = toNumber(valueFor(valuesById, primitive.id, primitive.value), primitive.value);
    const decrement = () => setValue(Math.max(toNumber(primitive.min), current - toNumber(primitive.step, 1)));
    const increment = () => setValue(Math.min(toNumber(primitive.max), current + toNumber(primitive.step, 1)));
    return (
      <div className="composable-stepper">
        <div>
          <span>{primitive.label}</span>
          <strong>{formatPrimitiveValue(current, primitive.format)}</strong>
        </div>
        <div className="composable-stepper-actions">
          <button type="button" data-testid={`composable-stepper-${primitive.id}-decrement`} onClick={decrement}>-</button>
          <button type="button" data-testid={`composable-stepper-${primitive.id}-increment`} onClick={increment}>+</button>
        </div>
      </div>
    );
  }

  if (primitive.type === 'callout') {
    return (
      <div className={`composable-callout ${primitive.tone || 'info'}`}>
        <strong>{primitive.title}</strong>
        <p>{primitive.body}</p>
      </div>
    );
  }

  if (primitive.type === 'table_summary') {
    return (
      <div className="composable-table composable-wide">
        <div className="interactive-label">{primitive.label}</div>
        <table>
          <thead>
            <tr>{(primitive.columns || []).map((column) => <th key={column.key}>{column.label}</th>)}</tr>
          </thead>
          <tbody>
            {(primitive.rows || []).map((row, index) => (
              <tr key={index}>
                {(primitive.columns || []).map((column) => (
                  <td key={column.key}>
                    {column.key === 'value' && row.value_id
                      ? formatPrimitiveValue(valueFor(valuesById, row.value_id, row[column.key]), row.format)
                      : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return <div className="embed-fallback composable-wide">Unsupported primitive: {primitive.type}</div>;
}

const TEMPLATE_MAP = {
  emergency_fund: EmergencyFundApplet,
  debt_payoff: DebtPayoffApplet,
  retirement_contribution: RetirementContributionApplet,
  compound_growth: CompoundGrowthApplet,
  diversification_risk: DiversificationRiskApplet,
  spending_cash_flow: SpendingCashFlowApplet,
  goal_progress: GoalProgressApplet,
  rent_vs_buy: RentVsBuyApplet,
  portfolio_allocation: PortfolioAllocationApplet,
  tax_impact: TaxImpactApplet,
  insurance_coverage: InsuranceCoverageApplet,
  retirement_withdrawal: RetirementWithdrawalApplet,
  composable: ComposableApplet,
};

export default function InteractiveEmbed({ template, ...embed }) {
  const Comp = TEMPLATE_MAP[template];
  if (!Comp) {
    return (
      <div className="embed-fallback" role="status">
        {embed.title && <div className="embed-fallback-title">{embed.title}</div>}
        {embed.summary && <div className="embed-fallback-summary">{embed.summary}</div>}
        <div>This interactive view could not be displayed.</div>
      </div>
    );
  }

  return <Comp embed={{ ...embed, template }} />;
}
